import { useCallback, useEffect, useRef, type ReactNode } from 'react';

import type { SessionStore } from './create-session-store';
import type { SessionClientApi, SessionTimingConfig } from './types';

const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'] as const;

export const DEFAULT_SESSION_CHANNEL_NAME = 'dawn-session';

export interface SessionProviderProps<TUser> {
  /** The app's session store (one `createSessionStore` instance per app). */
  store: SessionStore<TUser>;
  /** Timer thresholds derived from the app's /config session policy; null while config loads. */
  timing: SessionTimingConfig | null;
  /** `me` + `ping` adapters over the app's API client. Must be referentially stable. */
  api: SessionClientApi<TUser>;
  /**
   * Where to send the user when the session is over: after the expired-modal
   * auto-redirect fires, or when another tab broadcasts a logout/redirect.
   * (Dashboard: navigate('/login'); onboarding: reload so the landing resolve
   * re-routes to the OTP flow.)
   */
  onSignedOut: () => void;
  /** BroadcastChannel name for cross-tab sync; override per app if needed. */
  channelName?: string;
  children: ReactNode;
}

/**
 * The client half of the shared session model (server: @dawn/nestjs/session).
 * Owns every session timer for the app:
 *
 * - initializes by probing `api.me()` and seeding the store;
 * - tracks real user activity and extends the session (`api.ping()`) at most
 *   once per `pingIntervalMs` — fire-and-forget;
 * - warns before the inactivity timeout, then verifies against the backend
 *   before declaring inactivity expiry (the server is the source of truth);
 * - warns before and enforces the hard max-age deadline;
 * - after the expired modal has been up `autoRedirectMs`, signs out;
 * - syncs logout / activity / login across tabs via a BroadcastChannel.
 *
 * Extracted from the dashboard's provider (TDEV-073) — behavior is identical,
 * with the app-specific parts (store, API calls, redirect target) injected.
 */
export function SessionProvider<TUser>({
  store,
  timing,
  api,
  onSignedOut,
  channelName = DEFAULT_SESSION_CHANNEL_NAME,
  children,
}: SessionProviderProps<TUser>) {
  const lastActivityAt = useRef(Date.now());
  const lastPingSentAt = useRef(0);
  const channelRef = useRef<BroadcastChannel | null>(null);

  const status = store((s) => s.status);
  const maxAllowedTime = store((s) => s.maxAllowedTime);
  const modalShownAt = store((s) => s.modalShownAt);
  const setActive = store((s) => s.setActive);
  const setInactiveWarning = store((s) => s.setInactiveWarning);
  const setMaxAgeWarning = store((s) => s.setMaxAgeWarning);
  const setExpired = store((s) => s.setExpired);
  const setUnauthenticated = store((s) => s.setUnauthenticated);
  const resetActivity = store((s) => s.resetActivity);

  // --- Initialize session on mount ---
  useEffect(() => {
    if (status !== 'initializing') return;
    let cancelled = false;

    api
      .me()
      .then((snapshot) => {
        if (cancelled) return;
        if (snapshot) {
          lastActivityAt.current = Date.now();
          setActive(snapshot.user, snapshot.sessionCreatedAt, snapshot.maxAllowedTime);
        } else {
          setUnauthenticated();
        }
      })
      .catch(() => {
        // Network failures also mean unauthenticated
        if (!cancelled) setUnauthenticated();
      });

    return () => {
      cancelled = true;
    };
  }, [status, api, setActive, setUnauthenticated]);

  // --- Activity tracking ---
  const handleActivity = useCallback(() => {
    const now = Date.now();
    if (now - lastActivityAt.current < 1000) return;
    lastActivityAt.current = now;

    const currentStatus = store.getState().status;
    if (currentStatus === 'inactive_warning') {
      resetActivity();
      lastPingSentAt.current = now;
      api.ping().catch(() => {
        // Fire-and-forget per Decision F2-11
      });
      channelRef.current?.postMessage({ type: 'activity' });
    } else if (
      timing &&
      (currentStatus === 'active' || currentStatus === 'max_age_warning') &&
      now - lastPingSentAt.current >= timing.pingIntervalMs
    ) {
      // Overdue ping — fire immediately instead of waiting for next interval tick
      lastPingSentAt.current = now;
      api.ping().catch(() => {
        // Fire-and-forget per Decision F2-11
      });
    }
  }, [store, api, resetActivity, timing]);

  useEffect(() => {
    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, handleActivity, { passive: true });
    }
    return () => {
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, handleActivity);
      }
    };
  }, [handleActivity]);

  // --- Backend verification before inactivity expiry ---
  const verifyAndExpireInactivity = useCallback(async () => {
    try {
      const snapshot = await api.me();
      if (snapshot) {
        // Backend session is still valid — restore active state
        lastActivityAt.current = Date.now();
        setActive(snapshot.user, snapshot.sessionCreatedAt, snapshot.maxAllowedTime);
      } else {
        setExpired('inactivity');
      }
    } catch {
      // Backend confirmed session is invalid
      setExpired('inactivity');
    }
  }, [api, setActive, setExpired]);

  // --- Inactivity timer ---
  useEffect(() => {
    if (status !== 'active' && status !== 'inactive_warning') return;
    if (!timing) return;

    const interval = setInterval(() => {
      const elapsed = Date.now() - lastActivityAt.current;

      if (elapsed >= timing.inactivityTimeoutMs) {
        verifyAndExpireInactivity();
      } else if (elapsed >= timing.inactivityWarningMs) {
        if (store.getState().status === 'active') {
          setInactiveWarning();
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [status, timing, store, verifyAndExpireInactivity, setInactiveWarning]);

  // --- Max-age timer ---
  useEffect(() => {
    if (status !== 'active' && status !== 'max_age_warning') return;
    if (!maxAllowedTime) return;
    if (!timing) return;

    const warningThreshold = maxAllowedTime - (timing.maxDurationMs - timing.maxDurationWarningMs);

    const interval = setInterval(() => {
      const now = Date.now();

      if (now >= maxAllowedTime) {
        setExpired('max_age');
      } else if (now >= warningThreshold) {
        const currentStatus = store.getState().status;
        if (currentStatus === 'active' || currentStatus === 'inactive_warning') {
          setMaxAgeWarning();
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [status, timing, store, maxAllowedTime, setExpired, setMaxAgeWarning]);

  // --- Auto-redirect timer ---
  useEffect(() => {
    if (status !== 'expired' || !modalShownAt) return;
    if (!timing) return;

    const interval = setInterval(() => {
      if (Date.now() - modalShownAt >= timing.autoRedirectMs) {
        setUnauthenticated();
        channelRef.current?.postMessage({ type: 'forced_redirect' });
        onSignedOut();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [status, timing, modalShownAt, setUnauthenticated, onSignedOut]);

  // Heartbeat pings are handled by handleActivity — pings fire at most once
  // per pingIntervalMs, only when real user activity is detected.

  // --- Visibility change handler ---
  useEffect(() => {
    if (status !== 'active' && status !== 'inactive_warning' && status !== 'max_age_warning') {
      return;
    }
    if (!timing) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      const now = Date.now();
      const elapsed = now - lastActivityAt.current;
      const currentMaxAllowedTime = store.getState().maxAllowedTime;

      // Recheck inactivity thresholds
      if (elapsed >= timing.inactivityTimeoutMs) {
        verifyAndExpireInactivity();
        return;
      }
      if (elapsed >= timing.inactivityWarningMs) {
        if (store.getState().status === 'active') {
          setInactiveWarning();
        }
      }

      // Recheck max-age thresholds
      if (currentMaxAllowedTime) {
        if (now >= currentMaxAllowedTime) {
          setExpired('max_age');
          return;
        }
        const warningThreshold =
          currentMaxAllowedTime - (timing.maxDurationMs - timing.maxDurationWarningMs);
        if (now >= warningThreshold) {
          const currentStatus = store.getState().status;
          if (currentStatus === 'active' || currentStatus === 'inactive_warning') {
            setMaxAgeWarning();
          }
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [
    status,
    timing,
    store,
    verifyAndExpireInactivity,
    setInactiveWarning,
    setMaxAgeWarning,
    setExpired,
  ]);

  // --- BroadcastChannel ---
  useEffect(() => {
    const channel = new BroadcastChannel(channelName);
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent<{ type: string }>) => {
      const { type } = event.data;

      if (type === 'logout' || type === 'forced_redirect') {
        setUnauthenticated();
        onSignedOut();
      } else if (type === 'activity') {
        lastActivityAt.current = Date.now();
        resetActivity();
      } else if (type === 'login') {
        api
          .me()
          .then((snapshot) => {
            if (snapshot) {
              lastActivityAt.current = Date.now();
              setActive(snapshot.user, snapshot.sessionCreatedAt, snapshot.maxAllowedTime);
            }
          })
          .catch(() => {
            // Ignore — tab will handle its own auth state
          });
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [channelName, api, onSignedOut, setUnauthenticated, setActive, resetActivity]);

  return <>{children}</>;
}
